import React, { useState } from 'react';
import Card from 'components/card';
import { IRole } from 'views/types/permissions';
import { ResourcePermissions } from '../ResourcePermissions';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { MdExpandMore, MdExpandLess, MdDelete } from 'react-icons/md';
import { DeleteRoleConfirmDialog } from '../DeleteRoleConfirmDialog';
import { DeletePermissionGate } from 'components/PermissionGate';
import { usePermissions } from 'hooks/usePermissions';
import {
  RESOURCE_CATEGORIES,
  RESOURCE_DISPLAY_NAMES,
  PERMISSION_RESOURCES,
} from 'constants/permissions';
import { formatRoleName } from 'utils/formatRoleName';
import { useIsSuperAdmin } from 'hooks/useIsSuperAdmin';

interface RolePermissionsCardProps {
  role: IRole;
  onUpdate: (roleId: string, resource: string, actions: string[]) => void;
  onDeleteSuccess?: () => void;
  isUpdating?: boolean;
}

const getResourceDisplayName = (resource: string): string => {
  return RESOURCE_DISPLAY_NAMES[resource] || resource;
};

export const RolePermissionsCard: React.FC<RolePermissionsCardProps> = ({
  role,
  onUpdate,
  onDeleteSuccess,
  isUpdating = false,
}) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );
  const { canUpdate } = usePermissions();
  const isSuperAdmin = useIsSuperAdmin();

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const getFilteredResourceCategories = () => {
    if (isSuperAdmin) {
      return RESOURCE_CATEGORIES;
    }

    const filteredCategories = {
      ...RESOURCE_CATEGORIES,
      'User Management': RESOURCE_CATEGORIES['User Management'].filter(
        resource => resource !== PERMISSION_RESOURCES.PERMISSION_MANAGEMENT
      ),
    };

    return filteredCategories;
  };

  return (
    <Card extra="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
            {formatRoleName(role.name)}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {role.description}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {isUpdating && (
            <AiOutlineLoading3Quarters className="h-5 w-5 animate-spin text-blue-500" />
          )}
          {onDeleteSuccess && role.name !== 'super_admin' && (
            <DeletePermissionGate
              resource={PERMISSION_RESOURCES.PERMISSION_MANAGEMENT}
            >
              <DeleteRoleConfirmDialog
                roleId={role.id}
                roleName={formatRoleName(role.name)}
                onSuccess={onDeleteSuccess}
              >
                <button
                  className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete role"
                >
                  <MdDelete className="h-5 w-5" />
                </button>
              </DeleteRoleConfirmDialog>
            </DeletePermissionGate>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {Object.entries(getFilteredResourceCategories()).map(
          ([category, resources]) => {
            const isExpanded = expandedCategories.has(category);

            return (
              <div
                key={category}
                className="border border-gray-200 dark:border-gray-700 rounded-lg"
              >
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full px-4 py-3 text-left bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg flex items-center justify-between"
                >
                  <span className="font-medium text-gray-800 dark:text-white">
                    {category}
                  </span>
                  {isExpanded ? (
                    <MdExpandLess className="h-5 w-5 text-gray-500" />
                  ) : (
                    <MdExpandMore className="h-5 w-5 text-gray-500" />
                  )}
                </button>

                {isExpanded && (
                  <div className="p-4 space-y-3">
                    {resources.map((resource: string) => {
                      const resourcePermissions = (
                        role.permissions as unknown as Record<
                          string,
                          Record<string, boolean>
                        >
                      )?.[resource];
                      const actions = resourcePermissions
                        ? Object.keys(resourcePermissions).filter(
                            action => resourcePermissions[action]
                          )
                        : [];

                      return (
                        <ResourcePermissions
                          key={resource}
                          resourceName={getResourceDisplayName(resource)}
                          resource={resource}
                          permissions={actions}
                          onChange={newActions =>
                            onUpdate(role.id, resource, newActions)
                          }
                          disabled={
                            isUpdating ||
                            !canUpdate(
                              PERMISSION_RESOURCES.PERMISSION_MANAGEMENT
                            )
                          }
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
        )}
      </div>
    </Card>
  );
};
